// The phone's canvas by touch (spec docs/specs/editor/phone-canvas.md): a finger held still on the
// frame opens the canvas menu, as a right-click does. The move, the resize and the turn stay the
// frame's own pointer gestures (`Preview`'s press, `CanvasHandles`), sized for a finger by the
// phone's layer in `editor.module.css`.
import { useEffect, useRef, type RefObject } from "react";

import { LONG_PRESS_MS, LONG_PRESS_SLOP_PX } from "./touch-timeline";

export interface CanvasTouch {
  /** Stops the move the finger's press began: the item stays where it was. */
  onHold: () => void;
  /** Opens the canvas menu at the finger, on what is drawn on top there, as a right-click does. */
  onMenu: (clientX: number, clientY: number) => void;
}

/** Mounted by `Preview` on a phone, given its frame; `options` undefined off a phone does nothing. */
export function useCanvasTouch(frame: RefObject<HTMLElement | null>, options: CanvasTouch | undefined): void {
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });
  const on = options !== undefined;

  useEffect(() => {
    const element = frame.current;
    if (!on || element === null) return undefined;
    let press: { x: number; y: number; timer: number } | null = null;
    // From a touch's start to its last finger's lift: the phone's own long-press menu is held off.
    let touching = false;
    // The menu opened while the finger was down: its lift is no click.
    let opened = false;
    const letGo = () => {
      if (press !== null) window.clearTimeout(press.timer);
      press = null;
    };
    const held = (x: number, y: number) => () => {
      press = null;
      const now = latest.current;
      if (now === undefined) return;
      opened = true;
      now.onHold();
      now.onMenu(x, y);
    };
    const start = (event: TouchEvent) => {
      touching = true;
      opened = false;
      letGo();
      const touch = event.touches[0];
      if (event.touches.length !== 1 || touch === undefined) return;
      // A finger on a handle or a turn zone is the resize's or the turn's, however long it rests.
      if (event.target instanceof Element && event.target.closest("[data-handle], [data-turn]") !== null) return;
      press = { x: touch.clientX, y: touch.clientY, timer: window.setTimeout(held(touch.clientX, touch.clientY), LONG_PRESS_MS) };
    };
    const move = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (press === null || touch === undefined) return;
      // A finger that moves first is the move's, and a second finger is no long-press.
      if (event.touches.length !== 1 || Math.hypot(touch.clientX - press.x, touch.clientY - press.y) >= LONG_PRESS_SLOP_PX) {
        letGo();
      }
    };
    const end = (event: TouchEvent) => {
      letGo();
      if (event.touches.length > 0) return;
      touching = false;
      // The menu closes on a click outside it: the lift that opened it sends none.
      if (opened && event.cancelable) event.preventDefault();
      opened = false;
    };
    const noMenu = (event: Event) => {
      if (!touching) return;
      event.preventDefault();
      event.stopPropagation();
    };
    element.addEventListener("touchstart", start, { passive: true });
    element.addEventListener("touchmove", move, { passive: true });
    element.addEventListener("touchend", end, { passive: false });
    element.addEventListener("touchcancel", end);
    element.addEventListener("contextmenu", noMenu, true);
    return () => {
      letGo();
      element.removeEventListener("touchstart", start);
      element.removeEventListener("touchmove", move);
      element.removeEventListener("touchend", end);
      element.removeEventListener("touchcancel", end);
      element.removeEventListener("contextmenu", noMenu, true);
    };
  }, [on, frame]);
}
